import {
  IconAdjustments,
  IconChartDots3,
  IconChartPie2,
  IconDatabase,
  IconExternalLink,
  IconHome2,
  IconKey,
  IconMessage,
  IconMessages,
  IconPlayerPlay,
  IconPuzzle,
  IconTrendingUp,
  IconUserScan,
} from '@tabler/icons-react';
import { Badge } from 'design-system/badge/badge';
import { GetStartedNavItem } from 'features/onboarding/components/get-started-nav-item';
import { countCompletedSteps } from 'features/onboarding/get-started-steps';
import { useGetStartedProgress } from 'features/onboarding/hooks/use-get-started-progress';
import { useAuth } from 'lib/auth';
import { cn } from 'lib/cn';
import { CHAT_BASE_URL } from 'lib/constants/chat-url';
import type React from 'react';
import { Link, useLocation } from 'react-router';
import { routes } from 'routes/routes';
import {
  type ProjectLevelPermissions,
  type TeamLevelPermissions,
  type UserTeam,
} from 'types/auth';
import { SubscriptionTier } from 'types/team';

import { useSidebarUI } from './sidebar-context';
import { SidebarFooter } from './sidebar-footer';
import { SidebarHeader } from './sidebar-header';
import { SidebarLayout } from './sidebar-layout';
import { SidebarNavItem } from './sidebar-nav-item';
import { useAskAi } from './use-ask-ai';

type IconComponent = React.ComponentType<{
  size?: number | string;
  stroke?: number | string;
  className?: string;
  color?: string;
}>;

type ProjectNavRouteKey =
  | 'dashboard'
  | 'conversations'
  | 'users'
  | 'coverageGaps'
  | 'topQuestions'
  | 'sourceAnalytics'
  | 'sources'
  | 'integrations'
  | 'customizations'
  | 'apiKeys'
  | 'playground';

interface NavItem {
  link?: string;
  route?: ProjectNavRouteKey;
  label: string;
  icon: IconComponent;
  disabled: boolean;
  prependProjectId: boolean;
  requiresSubscriptionFlags: (keyof UserTeam['subscription_settings'])[];
  teamLevelPermissionRequired: TeamLevelPermissions | null;
  projectLevelPermissionRequired: ProjectLevelPermissions | null;
  badge: string | null;
  externalLink: boolean;
  isHiddenOnFreeTier?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface NavContent {
  main: NavItem[];
  sections: NavSection[];
}

const NAVBAR_CONTENT: NavContent = {
  main: [
    {
      route: 'dashboard',
      label: 'Dashboard',
      icon: IconHome2,
      disabled: false,
      prependProjectId: true,
      requiresSubscriptionFlags: [],
      teamLevelPermissionRequired: null,
      projectLevelPermissionRequired: 'read_project',
      badge: null,
      externalLink: false,
    },
    {
      link: `${CHAT_BASE_URL}/[project]`,
      label: 'Chat',
      icon: IconMessage,
      disabled: false,
      prependProjectId: false,
      requiresSubscriptionFlags: [],
      teamLevelPermissionRequired: null,
      projectLevelPermissionRequired: null,
      badge: null,
      externalLink: true,
      isHiddenOnFreeTier: true,
    },
  ],
  sections: [
    {
      title: 'Configure',
      items: [
        {
          route: 'sources',
          label: 'Sources',
          icon: IconDatabase,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'customizations',
          label: 'Customizations',
          icon: IconAdjustments,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
      ],
    },
    {
      title: 'Deploy',
      items: [
        {
          route: 'playground',
          label: 'Playground',
          icon: IconPlayerPlay,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'integrations',
          label: 'Integrations',
          icon: IconPuzzle,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'apiKeys',
          label: 'API Keys',
          icon: IconKey,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
      ],
    },
    {
      title: 'Analyze',
      items: [
        {
          route: 'conversations',
          label: 'Conversations',
          icon: IconMessages,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'users',
          label: 'Users',
          icon: IconUserScan,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'coverageGaps',
          label: 'Coverage Gaps',
          icon: IconChartDots3,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'topQuestions',
          label: 'Top Questions',
          icon: IconTrendingUp,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
        {
          route: 'sourceAnalytics',
          label: 'Source Analytics',
          icon: IconChartPie2,
          disabled: false,
          prependProjectId: true,
          requiresSubscriptionFlags: [],
          teamLevelPermissionRequired: null,
          projectLevelPermissionRequired: 'read_project',
          badge: null,
          externalLink: false,
        },
      ],
    },
  ],
};

const useActiveNavbarItem = () => {
  const location = useLocation();
  // Read the project from auth, not useParams: NavBar also renders inside the
  // app-level MobileNavDrawer (mounted above the routes), where useParams is
  // empty and project-scoped links would never match the active route. This
  // mirrors the source used to build each item's href below.
  const auth = useAuth();
  const projectId = auth.project.selected?.id;
  const pathname = location.pathname;

  const allItems = [
    ...NAVBAR_CONTENT.main,
    ...NAVBAR_CONTENT.sections.flatMap((section) => section.items),
  ];

  for (const item of allItems) {
    if (item.externalLink) {
      continue;
    }

    if (item.route && !projectId) continue;
    const link = item.route
      ? routes[item.route].build({ project: projectId! })
      : (item.link ?? '');

    if (item.route === 'dashboard' && projectId) {
      const dashboardPath = routes.dashboard.build({ project: projectId });
      if (pathname === dashboardPath || pathname === `${dashboardPath}/`) {
        return item;
      }
      continue;
    }

    if (pathname === link || pathname.startsWith(`${link}/`)) {
      return item;
    }
  }

  return null;
};

interface NavBarProps {
  /** When rendered inside the mobile drawer, always show the full sidebar. */
  inDrawer?: boolean;
  /** Drawer override: swap the drawer view instead of navigating to settings. */
  onSettingsSelect?: () => void;
}

export const NavBar = ({ inDrawer = false, onSettingsSelect }: NavBarProps) => {
  const auth = useAuth();
  const {
    data: getStartedProgress,
    isVisible: isGetStartedVisible,
    isError: isGetStartedError,
  } = useGetStartedProgress();
  const { pathname } = useLocation();
  const projectId = auth.project.selected?.id;
  const activeNavbarItem = useActiveNavbarItem();
  const onAskAi = useAskAi();
  const { toggle, isDesktop, isCollapsed } = useSidebarUI();
  const collapsed = inDrawer ? false : !isDesktop || isCollapsed;
  const teamName = auth.user?.user.team.name ?? '';

  const renderNavItem = (item: NavItem, collapsed: boolean) => {
    if (item.route && !auth.project.selected?.id) return null;
    if (
      item.isHiddenOnFreeTier &&
      auth.user?.user.team.tier === SubscriptionTier.Free
    ) {
      return null;
    }
    let disabled: boolean = item.disabled;
    item.requiresSubscriptionFlags.forEach((flag) => {
      if (auth.user?.user.team.subscription_settings?.[flag] === false) {
        disabled = true;
      }
    });
    if (
      item.teamLevelPermissionRequired &&
      !auth.user?.user.team.permissions.all.includes(
        item.teamLevelPermissionRequired,
      )
    ) {
      disabled = true;
    }
    if (
      item.projectLevelPermissionRequired &&
      !auth.project.selected?.permissions.all.includes(
        item.projectLevelPermissionRequired,
      )
    ) {
      disabled = true;
    }
    const link = () => {
      if (item.externalLink) {
        return (item.link ?? '').replace(
          '[project]',
          auth.project.selected?.id ?? '',
        );
      }
      return item.route
        ? routes[item.route].build({
            project: auth.project.selected!.id,
          })
        : (item.link ?? '');
    };

    const isActive = activeNavbarItem === item;

    let rightSection: React.ReactNode = null;
    if (item.badge) {
      rightSection = (
        <Badge
          variant="secondary"
          className={cn(
            'shrink-0 transition-opacity',
            collapsed && 'opacity-0',
          )}
        >
          {item.badge}
        </Badge>
      );
    } else if (item.externalLink) {
      rightSection = (
        <span
          className={cn(
            'inline-flex shrink-0 opacity-0 transition-opacity',
            !collapsed && 'group-hover/nav-link:opacity-100',
          )}
        >
          <IconExternalLink size={16} stroke={1.5} />
        </span>
      );
    }

    return (
      <SidebarNavItem
        key={item.label}
        label={item.label}
        collapsed={collapsed}
        isActive={isActive}
        icon={item.icon}
        trailing={rightSection}
        className={cn(
          disabled &&
            'text-foreground-tertiary pointer-events-none bg-transparent',
        )}
        tooltip={
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            {item.label}
            {item.externalLink ? (
              <IconExternalLink size={14} stroke={1.5} />
            ) : null}
          </span>
        }
        render={
          <Link
            target={item.externalLink ? '_blank' : undefined}
            rel={item.externalLink ? 'noopener noreferrer' : undefined}
            to={disabled ? '#' : link()}
            aria-disabled={disabled || undefined}
            aria-label={item.label}
            tabIndex={disabled ? -1 : undefined}
            onClick={disabled ? (event) => event.preventDefault() : undefined}
          />
        }
      />
    );
  };

  return (
    <SidebarLayout
      collapsed={collapsed}
      slots={{
        header: (
          <SidebarHeader
            teamName={teamName}
            collapsed={collapsed}
            onToggle={toggle}
          />
        ),
        body: (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1.5 pb-3">
              {isGetStartedVisible && projectId && (
                <GetStartedNavItem
                  render={
                    <Link
                      to={routes.getStarted.build({ project: projectId })}
                      aria-label="Get started"
                    />
                  }
                  isActive={
                    pathname === routes.getStarted.build({ project: projectId })
                  }
                  collapsed={collapsed}
                  completedCount={
                    getStartedProgress && !isGetStartedError
                      ? countCompletedSteps(getStartedProgress)
                      : undefined
                  }
                />
              )}
              {NAVBAR_CONTENT.main.map((item) =>
                renderNavItem(item, collapsed),
              )}
            </div>
            {NAVBAR_CONTENT.sections.map((section) => (
              <div className="flex flex-col gap-1.5 pb-3" key={section.title}>
                <span
                  className={cn(
                    'text-foreground-secondary text-footnote pl-2 whitespace-nowrap transition-opacity',
                    collapsed && 'opacity-0',
                  )}
                >
                  {section.title}
                </span>
                {section.items.map((item) => renderNavItem(item, collapsed))}
              </div>
            ))}
          </div>
        ),
        footer: (
          <SidebarFooter
            onAskAi={onAskAi}
            onSettingsSelect={onSettingsSelect}
            collapsed={collapsed}
          />
        ),
      }}
    />
  );
};
